import { Button, ButtonGroup } from '@blueprintjs/core'
import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import TextAlign from '@tiptap/extension-text-align'
import CX from 'classnames'
import React, { useState, useEffect, useCallback } from 'react'
import Client from '../../client'
import { LoadableSelect } from '../Inputs'

type ProcessArg = {
  templates: {
    id: number
    title: string
  }[]
}

interface RichEditorProps {
  value: string
  onChange: (change: string) => void
  editorHeight?: number
  disabled?: boolean
  showTemplates?: boolean
  showTemplatesPosition?: string
  style?: React.CSSProperties
}

const RichEditor = (props: RichEditorProps) => {
  const { value, onChange, disabled } = props
  const { showTemplates, showTemplatesPosition, editorHeight, ...domFriendlyProps } = props
  const [templateValue, setTemplateValue] = useState<number>(-1)
  const [focused, setFocused] = useState<boolean>(false)
  const [lastFocusOnEditor, setLastFocusOnEditor] = useState<boolean>(false)
  const [, setSelectionTick] = useState<number>(0)

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        alignments: ['left', 'center', 'right']
      })
    ],
    content: value || '',
    editable: !disabled,
    onUpdate: args => {
      onChange(args.editor.getHTML())
    },
    onSelectionUpdate: () => {
      setSelectionTick(prev => prev + 1)
    }
  })

  const onProcess = useCallback((state: ProcessArg) => state.templates.map(s => ({ value: s.id, label: s.title })), [])

  const onSelectTemplate = useCallback(
    async (v: number) => {
      const body = await Client.getTemplate(v).then(response => response.data.template.body)

      if (lastFocusOnEditor && editor) {
        editor.commands.insertContent(body)
        setTemplateValue(v)
        analytics.track('Static Template Inserted')
        return
      }

      const updatedValue = typeof value !== 'string' ? body : `${value}<p>${body}</p>`
      onChange(updatedValue)

      if (editor) {
        setTimeout(() => {
          const editorDOM = editor.view.dom
          if (editorDOM) {
            editorDOM.scrollTop = editorDOM.scrollHeight
          }
        }, 50)
      }

      setTemplateValue(v)
      analytics.track('Static Template Inserted')
    },
    [editor, lastFocusOnEditor, onChange, value]
  )

  const renderTemplates = useCallback(() => {
    const style: React.CSSProperties =
      showTemplatesPosition === 'bottom'
        ? {
            position: 'absolute',
            bottom: '8px',
            right: '16px',
            width: '50%'
          }
        : {
            marginLeft: 'auto',
            width: '30%'
          }

    return (
      <div style={style}>
        <LoadableSelect
          value={templateValue}
          onChange={onSelectTemplate}
          clearable={false}
          load={() => Client.templateIndex({ draft: false, static: true })}
          placeholder="Insert Template"
          process={onProcess}
          disabled={disabled}
          onFocus={() => {
            if (focused) setLastFocusOnEditor(() => true)
          }}
          onBlur={() => setLastFocusOnEditor(() => false)}
          autoload
          noLabel
        />
      </div>
    )
  }, [disabled, focused, onProcess, onSelectTemplate, templateValue, showTemplatesPosition])

  useEffect(
    function updateEditorState() {
      if (!editor) {
        return
      }

      if (editor.getHTML() !== value) {
        editor.commands.setContent(value ?? '', { emitUpdate: false })
      }
      editor.setEditable(!disabled)
    },
    [value, editor, disabled]
  )

  useEffect(
    function setupEventListeners() {
      const onBlur = () => setFocused(false)
      const onFocus = () => setFocused(true)

      if (editor) {
        editor.on('blur', onBlur)
        editor.on('focus', onFocus)
      }

      return () => {
        if (editor) {
          editor.off('blur', onBlur)
          editor.off('focus', onFocus)
        }
      }
    },
    [editor]
  )

  return (
    <div
      className={CX('wrapper', { templates: showTemplates })}
      style={{ position: 'relative', marginTop: '5px' }}
      {...domFriendlyProps}
      onChange={undefined}
    >
      {editor && !disabled && (
        <div style={{ marginBottom: '8px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <ButtonGroup>
            <Button
              icon="undo"
              minimal
              small
              onClick={() => editor.chain().focus().undo().run()}
              disabled={!editor.can().undo()}
              title="Undo"
            />
            <Button
              icon="redo"
              minimal
              small
              onClick={() => editor.chain().focus().redo().run()}
              disabled={!editor.can().redo()}
              title="Redo"
            />
          </ButtonGroup>
          <ButtonGroup>
            <Button
              icon="bold"
              minimal
              small
              active={editor.isActive('bold')}
              onClick={() => editor.chain().focus().toggleBold().run()}
              title="Bold"
            />
            <Button
              icon="italic"
              minimal
              small
              active={editor.isActive('italic')}
              onClick={() => editor.chain().focus().toggleItalic().run()}
              title="Italic"
            />
          </ButtonGroup>
          <ButtonGroup>
            <Button
              icon="align-left"
              minimal
              small
              active={editor.isActive({ textAlign: 'left' })}
              onClick={() => editor.chain().focus().setTextAlign('left').run()}
              title="Align Left"
            />
            <Button
              icon="align-center"
              minimal
              small
              active={editor.isActive({ textAlign: 'center' })}
              onClick={() => editor.chain().focus().setTextAlign('center').run()}
              title="Align Center"
            />
            <Button
              icon="align-right"
              minimal
              small
              active={editor.isActive({ textAlign: 'right' })}
              onClick={() => editor.chain().focus().setTextAlign('right').run()}
              title="Align Right"
            />
          </ButtonGroup>
          {showTemplates && showTemplatesPosition !== 'bottom' && renderTemplates()}
        </div>
      )}
      <EditorContent
        editor={editor}
        style={{
          minHeight: editorHeight || 300,
          border: '1px solid #d3d8de',
          padding: '10px',
          borderRadius: '3px',
          backgroundColor: disabled ? '#f5f8fa' : '#ffffff'
        }}
      />
      {showTemplates && showTemplatesPosition === 'bottom' && renderTemplates()}
    </div>
  )
}

export default RichEditor
